import type {
  FieldSet,
  Price,
  Product,
  ProductSibling,
  Value,
  Variant,
  Attributes,
} from '@scayle/storefront-nuxt'
import {
  getSizeFromVariant,
  getFirstAttributeValue,
  getProductSiblings,
  isInStock,
} from '@scayle/storefront-nuxt'
import type { LocationQuery } from 'vue-router'
import { colorConfig } from '~/config/defaultConfigs'
import {
  GIFT_CARD_ORDER_NUMBERS,
  PRODUCT_ARTICLE_TYPE_VALUE_PREMIUM_CLUB,
} from '#layers/base/constants/scayle'
import {
  DIRECT_ORDER_ATTRIBUTE_KEY,
  DIRECT_ORDER_YES_VALUE_ID,
} from '#layers/base/constants/search'
import { getSizeLabel } from '#layers/base/types/variant'
import {
  compareAlphabeticSizes,
  compareCupSizes,
  compareEuropeanNumericSizes,
} from '#layers/base/utils/filter'

const BadgeLabel = {
  NEW: 'new',
  SOLD_OUT: 'sold_out',
  ONLINE_EXCLUSIVE: 'online_exclusive',
  SUSTAINABLE: 'sustainable',
  PREMIUM: 'premium',
  DEFAULT: '',
} as const

type BadgeLabelParamsKeys =
  | 'isNew'
  | 'isSoldOut'
  | 'isOnlineOnly'
  | 'isSustainable'
  | 'isPremium'
type BadgeLabelParams = Partial<Record<BadgeLabelParamsKeys, boolean>>

export type ArticlePlacementEntry = {
  orderNumber?: string
  ppk?: string
}

export const getBadgeLabel = (params: BadgeLabelParams = {}): string => {
  if (!params) {
    return BadgeLabel.DEFAULT
  }
  const { isNew, isSoldOut, isOnlineOnly, isSustainable, isPremium } = params

  if (isNew) {
    return BadgeLabel.NEW
  } else if (isSoldOut) {
    return BadgeLabel.SOLD_OUT
  } else if (isOnlineOnly) {
    return BadgeLabel.ONLINE_EXCLUSIVE
  } else if (isSustainable) {
    return BadgeLabel.SUSTAINABLE
  } else if (isPremium) {
    return BadgeLabel.PREMIUM
  } else {
    return BadgeLabel.DEFAULT
  }
}

export const getColorCodeForId = (id: number) => {
  const colorMap: Record<number, string> = colorConfig.colorId

  if (colorMap[id]) {
    return colorMap[id]
  }

  return '#ffffff'
}

export const getIsQuantityChangeDisabled = (product?: Product) =>
  getFirstAttributeValue(product?.attributes, 'isQuantityChangeDisabled')
    ?.value === 'true'

export const getProductSizeCategories = (product: Product) => {
  return (
    product.variants?.reduce((data, variant) => {
      const sizeCategory = getSizeFromVariant(variant, 'sizeCategory')

      if (
        sizeCategory?.value &&
        data.every((item) => item.value !== sizeCategory.value)
      ) {
        return [...data, sizeCategory]
      }

      return data
    }, [] as Array<Value>) || []
  )
}

export const getLowestPrice = (variants: Variant[]): Price | undefined => {
  if (!variants?.length) {
    return undefined
  }
  return variants.reduce((m: Variant, x: Variant) => {
    if (!x.price || !isInStock(x)) {
      return m
    }
    if (!m.price) {
      return x
    }
    const currentPrice = m?.price?.withoutTax ?? m?.price?.withTax
    const nextPrice = x?.price?.withoutTax ?? x?.price?.withTax

    return currentPrice <= nextPrice ? m : x
  }, {} as Variant)?.price
}

export const getProductOrderNumber = (
  product?: Product,
): undefined | string => {
  if (!product) {
    return
  }

  // Prefer the goldner_-prefixed entry from mainOrderNumberList when present
  // (some categories ship special override numbers there). When the list isn't
  // populated (the SAPI response shape we observe in preprod), fall through to
  // the advanced-attribute scalar `mainOrderNumber` instead of returning early
  // — bailing here hid the Bestell-Nr. on every PDP.
  const mainOrderNumberList = product.attributes?.mainOrderNumberList?.values
  const goldnerOverride = Array.isArray(mainOrderNumberList)
    ? mainOrderNumberList.find((item: Value) =>
        item?.value?.toLowerCase().startsWith('goldner'),
      )?.value
    : undefined

  const goldnerStripped = goldnerOverride
    ? goldnerOverride.replace('goldner_', '').toUpperCase()
    : undefined

  return (
    goldnerStripped ??
    getAdvancedAttributeFirstValue(product, 'mainOrderNumber')?.toString()
  )
}

export const getFormattedOrderNumber = (
  product?: Product,
): undefined | string => {
  const orderNumber = getProductOrderNumber(product)
  if (!orderNumber) {
    return undefined
  }

  const order = orderNumber.toString()
  if (order.length !== 7) {
    if (order.startsWith('goldner')) {
      return order.replace('goldner_', '').toUpperCase()
    } else {
      return order
    }
  }

  if (order.startsWith('goldner')) {
    const strippedOrder = order.replace('goldner_', '').toUpperCase()
    return strippedOrder.slice(0, 4) + '-' + strippedOrder.slice(4)
  }

  return order.slice(0, 4) + '-' + order.slice(4)
}

export const formatOrderNumber = (orderNumber: string) => {
  return orderNumber.slice(0, 4) + '-' + orderNumber.slice(4)
}

/**
 * Extracts and formats the order number for a specific variant's product,
 * which may differ from its parent product's order number.
 * Each variant (e.g. different sizes/colors) can have its own unique order number stored in either:
 * - productOrderNumberList attribute (preferred)
 * - productOrderNumber attribute
 *
 * The function handles special "goldner_" prefixed numbers and formats all numbers with a hyphen (1234-5678).
 * Note: This does NOT resolve the parent product's order number, only the variant-specific one.
 *
 * @param variant - The product variant to extract the order number from
 * @returns The formatted variant-specific order number (XXXX-XXXX) or undefined if not found
 */
export const getProductOrderNumberFromVariant = (
  variant?: Variant,
): undefined | string => {
  // Prefer the list-shaped attribute when present (it carries `goldner_`-prefixed
  // overrides for special categories); otherwise fall back to the scalar
  // `productOrderNumber` attribute. The previous implementation returned early
  // when the list was missing, which left every variant's number empty in the
  // SAPI response shape we see in preprod.
  const listValues = variant?.attributes?.productOrderNumberList?.values
  const listGoldner = Array.isArray(listValues)
    ? listValues.find((item: Value) =>
        item?.value?.toLowerCase().startsWith('goldner'),
      )?.value
    : undefined

  const scalar = variant?.attributes?.productOrderNumber?.values
  const scalarValue = Array.isArray(scalar) ? scalar[0]?.value : scalar?.value

  const value = listGoldner ?? scalarValue
  if (value === undefined) {
    return undefined
  }

  if (value.startsWith('goldner')) {
    const strippedValue = value.replace('goldner_', '').toUpperCase()
    return strippedValue?.slice(0, 4) + '-' + strippedValue?.slice(4)
  }

  return value?.slice(0, 4) + '-' + value?.slice(4)
}

export const isPremiumClubProduct = (product: Product | null): boolean => {
  const articleTypeValue = getFirstAttributeValue(
    product?.attributes,
    'articleType',
  )?.value

  return articleTypeValue === PRODUCT_ARTICLE_TYPE_VALUE_PREMIUM_CLUB
}

export const isExcludedFromSearch = (product: Product): boolean => {
  const attribute = product.attributes?.[EXCLUDED_FROM_SEARCH_ATTRIBUTE_KEY]
  if (!attribute) {
    return false
  }
  const values = Array.isArray(attribute.values)
    ? attribute.values
    : [attribute.values]
  return values.some((value) => value?.id === EXCLUDED_FROM_SEARCH_VALUE_ID)
}

export const isDirectOrder = (
  // Accepts a Product or a ProductSibling. Typed structurally because the
  // ProductSibling type does not declare `attributes`, though it carries it at
  // runtime (siblings are fetched with the `directOrder` attribute).
  product: { attributes?: Product['attributes'] },
): boolean => {
  const attribute = product.attributes?.[DIRECT_ORDER_ATTRIBUTE_KEY]
  if (!attribute) {
    return false
  }
  const values = Array.isArray(attribute.values)
    ? attribute.values
    : [attribute.values]
  return values.some((value) => value?.id === DIRECT_ORDER_YES_VALUE_ID)
}

export const isGiftcard = (product: Product | null): boolean => {
  if (!product) {
    return false
  }
  const orderNumber = getProductOrderNumber(product) || ''

  return GIFT_CARD_ORDER_NUMBERS.includes(orderNumber)
}

export const getSiblingsColors = (product: Product): ProductSibling[] => {
  const colorHex = getProductSiblings(product, 'colorHex')
  const color = getProductSiblings(product, 'color')

  return colorHex
    .map((hex, index) => {
      if (!hex || !hex.colors) {
        return null
      }
      const colors = hex.colors.map((c, i) => ({
        id: c.id,
        label: color[index]?.colors[i]?.label || c.label,
        value: c.value,
      }))
      return { ...hex, colors }
    })
    .filter((item) => item !== null)
}

export interface Size {
  id: number
  variant: Variant
  sizeCategory: string
  size?: string
}

export interface SizeCategory {
  categoryName: string
  sizes: Size
}

export interface ProductSizes {
  [key: string]: Size[]
}

export const getProductSizesFromVariants = (
  variants: Variant[] = [],
): ProductSizes => {
  // Order in which size-category tabs are rendered on the PDP.
  // Normal sizes must come first so they appear in the first tab and are
  // preselected by default (pickers use activeTab = 0 on mount).
  const categorySortList: string[] = ['ngroessen', 'kgroessen', 'ekgroessen']

  // Create a new object
  const sizes: Size[] = variants.map(
    (variant): Size => ({
      id: variant.id,
      variant,
      sizeCategory: (getFirstAttributeValue(variant.attributes, 'sizeCategory')
        ?.value || 'size') as string,
      size: getFirstAttributeValue(variant.attributes, 'erpSizeKey')?.value,
    }),
  )

  // Sort by category
  sizes.sort((a, b) => {
    return (
      categorySortList.indexOf(a.sizeCategory) -
      categorySortList.indexOf(b.sizeCategory)
    )
  })

  // Group by category
  const groupedByCategory = sizes.reduce(
    (result: Record<string, Size[]>, size) => {
      const bucket = result[size.sizeCategory] || []
      bucket.push(size)
      result[size.sizeCategory] = bucket
      return result
    },
    {},
  )

  for (const category in groupedByCategory) {
    const groupedSizes = groupedByCategory[category]
    if (!groupedSizes) {
      continue
    }

    groupedSizes.sort((a, b) => {
      const leftDisplay = getSizeLabel(a.variant)
      const rightDisplay = getSizeLabel(b.variant)

      // Cup sizes (e.g. "75B", "80C") must be ordered by band then cup letter,
      // not by erpSizeKey — historic ERP keys put B after E in some catalogs.
      const cupComparison = compareCupSizes(leftDisplay, rightDisplay)
      if (cupComparison !== 0) {
        return cupComparison
      }

      // EU numeric sizes (e.g. "EU38", "EU40/42") sort numerically
      const europeanComparison = compareEuropeanNumericSizes(
        leftDisplay,
        rightDisplay,
      )
      if (europeanComparison !== 0) {
        return europeanComparison
      }

      // Alphabetic sizes (XS, S, M, L, XL…) including combined sizes
      // (S/M, L/XL) — explicit ordering, since erpSizeKey and localeCompare
      // both put L/XL before S/M
      const alphabeticComparison = compareAlphabeticSizes(
        leftDisplay,
        rightDisplay,
      )
      if (alphabeticComparison !== 0) {
        return alphabeticComparison
      }

      const left = a.size ?? ''
      const right = b.size ?? ''

      const leftNum = Number(left)
      const rightNum = Number(right)
      if (!isNaN(leftNum) && !isNaN(rightNum)) {
        return leftNum - rightNum
      }

      return left.localeCompare(right)
    })
  }

  return groupedByCategory
}

export const getLowestPriceBetweenVariants = (product: Product) =>
  product.variants && getLowestPrice(product.variants)

export const attr = (p: Product | Variant | undefined | null, name: string) =>
  p?.attributes?.[name]?.values as Value | undefined

export const getProductOrderNumberList = (variant: Variant) => {
  const productOrderNumberList =
    variant.attributes?.productOrderNumberList?.values
  if (Array.isArray(productOrderNumberList)) {
    return productOrderNumberList
  }
  if (productOrderNumberList) {
    return [productOrderNumberList]
  }
  return []
}

export const getMatchingCupSizeVariant = (
  product: Product,
  orderNumber: string,
) => {
  return product.variants?.find((variant) => {
    const orderNumberListArray = getProductOrderNumberList(variant)

    return orderNumberListArray.find((item: any) => {
      const itemValue = item.value.split('_')[1]
      const orderNumberStriped = orderNumber?.toString().replace('-', '')

      return itemValue.toString() === orderNumberStriped
    })
  })
}

export const isCupSizeVariant = (variant: Variant): boolean => {
  // Extract the size attribute (e.g., "75 B" from "sizeCategorySize")
  const variantAttribute = getFirstAttributeValue(
    variant.attributes,
    'sizeCategorySize',
  )

  if (!variantAttribute?.label) {
    return false
  }

  // Check if the size format matches a cup size
  const labelParts = variantAttribute.label.split('|')
  const sizePart = labelParts[1]?.trim()
  const regex = /^\d+ ?[A-Za-z]$/

  return regex.test(sizePart || '')
}
export const getCupSizeMatchingArticlePlacement = (
  newOrderNumber: string,
  articlePlacements: FieldSet | undefined,
  promotionKey: string | undefined,
  defaultPromotionKey: string,
) => {
  const firstFiveDigits = newOrderNumber.replace('-', '').substring(0, 5)

  const findMatch = (key: string) =>
    articlePlacements?.find((articlePlacement: any) => {
      if (!Array.isArray(articlePlacement)) {
        return false
      }

      const placement = articlePlacement.flat()?.[0]?.value?.toString()
      if (!placement) {
        return false
      }

      const [orderNumber, ppk] = placement.split('|')
      return orderNumber.startsWith(firstFiveDigits) && ppk === key
    })

  const matchedPlacement = promotionKey
    ? findMatch(promotionKey)
    : findMatch(defaultPromotionKey)

  if (!matchedPlacement) {
    return undefined
  }

  const flattenedPlacement = matchedPlacement.flat()
  const placementValue = flattenedPlacement?.[0]?.value?.toString()

  if (!placementValue) {
    return undefined
  }

  return placementValue.split('|')[0]
}

export const getInitialPricePromotionKey = (
  product: Product,
  orderNumber: string | undefined,
): string | undefined => {
  const articlePlacements =
    product.advancedAttributes?.articlePlacement?.values[0]?.fieldSet

  const matchedOrderNumber = articlePlacements?.find(
    (articlePlacement: any) => {
      if (!Array.isArray(articlePlacement)) {
        return undefined
      }

      return (
        articlePlacement.flat()?.[0]?.value?.toString().split('|')[0] ===
        orderNumber?.toString()
      )
    },
  )

  // Return the promotion key if a match is found
  if (matchedOrderNumber && matchedOrderNumber[0]?.value) {
    return matchedOrderNumber[0].value.toString().split('|')[1]
  }

  return undefined
}

export const getSiblingOrderNoWithMatchingPromotionKey = (
  product: Product,
  id: number,
  initialPricePromotionKey: string | undefined,
): {
  matchingOrderNumber: string | undefined
  matchingPromotionKey: string | undefined
} => {
  const promotionKey = initialPricePromotionKey || DEFAULT_PRICE_PROMOTION_KEY

  const matchingItem =
    product.siblings?.find((sibling) => sibling.id === id) ||
    (id === product.id ? product : null)

  if (!matchingItem) {
    return { matchingOrderNumber: undefined, matchingPromotionKey: undefined }
  }

  const articlePlacements =
    matchingItem?.advancedAttributes?.articlePlacement?.values[0]?.fieldSet

  const matchingPlacement =
    findMatchingPlacement(articlePlacements, promotionKey) ||
    findMatchingPlacement(articlePlacements, DEFAULT_PRICE_PROMOTION_KEY)

  if (!matchingPlacement) {
    return { matchingOrderNumber: undefined, matchingPromotionKey: undefined }
  }

  const [extractedOrderNumber, extractedPromotionKey] =
    matchingPlacement.split('|')

  return {
    matchingOrderNumber: extractedOrderNumber,
    matchingPromotionKey: extractedPromotionKey,
  }
}

// Helper function to find matching article placement
const findMatchingPlacement = (
  placements: any,
  key: string,
): string | undefined => {
  const matchingPlacement = placements?.find((placement: any) => {
    if (!Array.isArray(placement)) {
      return undefined
    }

    return placement.flat()?.[0]?.value?.toString().split('|')[1] === key
  })
  return matchingPlacement?.[0]?.value?.toString()
}

export const getSizeNote = (
  product: Product,
  isBasket: boolean = false,
): string => {
  const sizeNoteValue =
    getFirstAttributeValue(product.attributes, 'sizeNote')?.value || ''

  if (!sizeNoteValue) {
    return ''
  }

  const prefix = isBasket ? 'basket' : 'pdp'

  if (sizeNoteValue.includes('groesser_aus')) {
    return `${prefix}.size_note.runs_large`
  } else if (sizeNoteValue.includes('kleiner_aus')) {
    return `${prefix}.size_note.runs_small`
  } else if (sizeNoteValue.includes('groessengetreu_aus')) {
    return `${prefix}.size_note.runs_true_to_size`
  }

  return ''
}

/**
 * Retrieves the article placement from the product.
 *
 * @param {Product} product - The product object.
 * @param {string} orderNumber - The order number to search for.
 * @returns {string | undefined} The article placement, or undefined if not found.
 */
export const getArticlePlacement = (product: Product, orderNumber: string) => {
  const articlePlacements =
    product.advancedAttributes?.articlePlacement?.values[0]?.fieldSet
  if (!articlePlacements) {
    return
  }

  return articlePlacements?.find((articlePlacement) =>
    articlePlacement[0]?.value?.toString().includes(`${orderNumber}|`),
  )
}

/**
 * Extracts all article placements from a product, returning order numbers and price promotion keys.
 *
 * @param {Product} product - The product object containing article placements.
 * @returns {ArticlePlacementEntry[]} Array of extracted order number and PPK pairs.
 */
export const extractArticlePlacements = (
  product: Product,
): ArticlePlacementEntry[] => {
  const articlePlacements =
    product.advancedAttributes?.articlePlacement?.values[0]?.fieldSet
  if (!articlePlacements) {
    return []
  }

  return articlePlacements.flat().map(({ value }) => {
    const [orderNumber, ppk] = (value?.toString() || '').split('|')
    return { orderNumber, ppk }
  })
}

/**
 * Finds an article placement entry matching the specified price promotion key.
 *
 * @param {Product} product - The product object.
 * @param {string} nominalPpk - The price promotion key to search for.
 * @returns {ArticlePlacementEntry | undefined} The matching article placement, or undefined if not found.
 */
export const getArticlePlacementByPpk = (
  product: Product,
  nominalPpk?: string,
): ArticlePlacementEntry | undefined => {
  const extractedAps = extractArticlePlacements(product)
  return extractedAps.find(({ ppk }) => ppk === nominalPpk)
}

/**
 * Finds an article placement entry matching the specified order number.
 *
 * @param {Product} product - The product object.
 * @param {string} nominalOrderNumber - The order number to search for.
 * @returns {ArticlePlacementEntry | undefined} The matching article placement, or undefined if not found.
 */
export const getArticlePlacementByOrderNumber = (
  product: Product,
  nominalOrderNumber?: string,
): ArticlePlacementEntry | undefined => {
  const extractedAps = extractArticlePlacements(product)
  return extractedAps.find(
    ({ orderNumber }) => orderNumber === nominalOrderNumber,
  )
}

/**
 * Finds an article placement for a variant by matching its order number prefix with the specified PPK.
 *
 * @param {Variant} variant - The product variant.
 * @param {Product} product - The parent product object.
 * @param {string} nominalPpk - The price promotion key to match.
 * @returns {ArticlePlacementEntry | undefined} The matching article placement, or undefined if not found.
 */
export const getVariantArticlePlacementByPpk = (
  variant: Variant,
  product: Product,
  nominalPpk?: string,
): ArticlePlacementEntry | undefined => {
  const extractedAps = extractArticlePlacements(product)

  const nominalOrderNumber = getProductOrderNumberFromVariant(variant)
  if (!nominalOrderNumber) {
    return
  }
  const orderNumberPrefix = nominalOrderNumber.replace('-', '').substring(0, 5)

  return extractedAps.find(({ orderNumber, ppk }) => {
    return orderNumber?.startsWith(orderNumberPrefix) && ppk === nominalPpk
  })
}

/**
 * Finds a variant's order number that matches the specified nominal order number.
 *
 * @param {Product} product - The product object containing variants.
 * @param {string} nominalOrderNumber - The order number to match against variants.
 * @returns {string | undefined} The matching variant's order number, or undefined if not found.
 */
export const getVariantBasedOrderNumber = (
  product: Product,
  nominalOrderNumber: string,
) => {
  const variant = product.variants?.find((variant) => {
    const variantOrderNumber = getProductOrderNumberFromVariant(variant)
    return variantOrderNumber === nominalOrderNumber
  })

  return variant ? getProductOrderNumberFromVariant(variant) : undefined
}

/**
 * Retrieves the price promotion key from the article placement.
 *
 * @param {Product} product - The product object.
 * @param {string} orderNumber - The order number to search for.
 * @returns {string | undefined} The price promotion key, or undefined if not found.
 */
export const getPricePromotionKey = (product: Product, orderNumber: string) => {
  const extractedAp = getArticlePlacementByOrderNumber(product, orderNumber)
  return extractedAp?.ppk
}

/**
 * Generates a route object for a product sibling with optional order number query parameter.
 *
 * @param {Product} sibling - The sibling product to create a route for.
 * @param {string} orderNumber - Optional order number to include in query parameters.
 * @returns {Object} The resolved route object for the sibling product.
 */
export const resolveSiblingRoute = (
  sibling: Product,
  orderNumber?: string | undefined,
) => {
  const query: LocationQuery = {}
  if (orderNumber) {
    query.ordernumber = orderNumber
  }
  return getProductDetailRoute(sibling, sibling.id, query)
}

/**
 * Finds a variant with the same size as the old variant in the new variants.
 *
 * @param {Variant[]} newVariants - The new variants to search through.
 * @param {Variant | undefined} oldVariant - The old variant to match against.
 * @returns {Variant | undefined} The matching variant, or undefined if not found.
 */
export const findMatchingSize = (
  newVariants: Variant[],
  oldVariant?: Variant,
) => {
  const oldSize = getFirstAttributeValue(oldVariant?.attributes, 'size')?.value
  if (!oldSize) {
    return
  }

  return newVariants.find((newVariant) => {
    const newSize = getFirstAttributeValue(newVariant.attributes, 'size')?.value
    return newSize === oldSize
  })
}

/**
 * Finds a variant with the same cup size as the old variant in the new variants.
 *
 * @param {Variant[]} newVariants - The new variants to search through.
 * @param {Variant | undefined} oldVariant - The old variant to match against.
 * @returns {Variant | undefined} The matching variant, or undefined if not found.
 */
export const findMatchingCupSize = (
  newVariants: Variant[],
  oldVariant?: Variant,
) => {
  const oldSize = getFirstAttributeValue(
    oldVariant?.attributes,
    'sizeCategorySize',
  )?.value
  if (!oldSize) {
    return
  }

  return newVariants.find((newVariant) => {
    const newSize = getFirstAttributeValue(
      newVariant.attributes,
      'sizeCategorySize',
    )?.value
    return newSize === oldSize
  })
}

export const firstAttributeValueLabel = (
  attrs: Attributes | undefined,
  attr: string,
) => getFirstAttributeValue(attrs, attr)?.label || ''
